import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import LoginModal from '../components/LoginModal';
import { useModal } from '../hooks/useModal';
import { useAuth } from '../context/AuthContext';
import { getCampaignDetail, scrapCampaign, unscrapCampaign } from '../api/campaigns';
import { useScraps } from '../context/ScrapContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { IconShare, IconBookmark } from '../components/Icons';
import { pushEvent } from '../utils/gtm';
import Toast from '../components/Toast';
import styles from './DetailPage.module.css';
import Breadcrumb from '../components/Breadcrumb';
import DetailSkeleton from '../components/DetailSkeleton';

const CHANNEL_LABEL = { BLOG: '블로그', INSTAGRAM: '인스타그램', YOUTUBE: '유튜브', OTHER: '기타' };

function fmt(d) {
  if (!d) return null;
  return d.split('T')[0].replace(/-/g, '.');
}

function formatDateRange(start, end) {
  const s = fmt(start), e = fmt(end);
  if (s && e) return `${s} ~ ${e}`;
  if (s) return `${s} ~`;
  if (e) return `~ ${e}`;
  return null;
}

/**
 * 상세 API는 마감일을 날짜 필드로 주지 않고 recruitPeriod 문자열
 * ("2026.08.28(금) ~ 2026.09.06(일)")만 준다. 마지막 날짜를 뽑아 D-day를 계산하고,
 * 형식이 다르면 조용히 건너뛴다.
 */
function calcDday(recruitPeriod) {
  const dates = recruitPeriod?.match(/\d{4}\.\d{1,2}\.\d{1,2}/g);
  if (!dates?.length) return null;
  const [y, m, d] = dates[dates.length - 1].split('.').map(Number);
  const end = new Date(y, m - 1, d);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
}

/** 메인 이미지(스와이프) + 썸네일. 썸네일 클릭 시 해당 이미지로 이동. */
function Gallery({ urls }) {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const handleScroll = () => {
    const el = trackRef.current;
    if (!el || !el.offsetWidth) return;
    setActive(Math.round(el.scrollLeft / el.offsetWidth));
  };

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.offsetWidth, behavior: 'smooth' });
  };

  if (urls.length === 0) {
    return (
      <div className={styles.gallery}>
        <div className={styles.fallback} />
      </div>
    );
  }

  return (
    <div className={styles.gallery}>
      <div className={styles.track} ref={trackRef} onScroll={handleScroll}>
        {urls.map((url, i) => (
          <div key={i} className={styles.slide}>
            <img src={url} alt={`이미지 ${i + 1}`} className={styles.slideImg} />
          </div>
        ))}
      </div>

      {urls.length > 1 && (
        <>
          <div className={styles.dots}>
            {urls.map((_, i) => (
              <span key={i} className={`${styles.dot}${i === active ? ` ${styles.dotActive}` : ''}`} />
            ))}
          </div>
          <div className={styles.thumbs}>
            {urls.map((url, i) => (
              <button
                key={i}
                className={`${styles.thumb}${i === active ? ` ${styles.thumbActive}` : ''}`}
                onClick={() => goTo(i)}
                aria-label={`이미지 ${i + 1} 보기`}
              >
                <img src={url} alt="" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function DetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { isScrapped: isScrappedFn, scrap, unscrap } = useScraps();
  const { isOpen, open, close } = useModal();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Toast의 자동 닫힘 타이머가 매 렌더마다 초기화되지 않도록 참조를 고정한다.
  const dismissToast = useCallback(() => setToast(null), []);

  const isScrapped = isScrappedFn(id);

  // 공유·검색결과에 체험단별로 다른 제목·설명·썸네일이 나가도록 한다.
  // 불러오기 전이나 실패했을 때는 색인에서 빼야 빈 페이지가 검색에 잡히지 않는다.
  const firstImage = campaign?.imageUrls?.[0] ?? campaign?.images?.[0];
  useDocumentMeta({
    title: campaign?.title,
    description: campaign
      ? [campaign.brandName, campaign.recruitCount && `${campaign.recruitCount}명 모집`]
          .filter(Boolean).join(' · ')
      : undefined,
    image: typeof firstImage === 'string' ? firstImage : firstImage?.url,
    noIndex: !campaign,
  });

  useEffect(() => {
    setLoading(true);
    setError(null);
    getCampaignDetail(id)
      .then((data) => {
        setCampaign(data);
        pushEvent('campaign_detail_view', { campaign_id: id, campaign_title: data?.title });
      })
      .catch(() => setError('체험단 정보를 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [id, reloadKey]);

  const handleBookmark = () => {
    if (!isLoggedIn) { open(); return; }
    return toggleScrap();
  };

  // 인증 검사와 분리한다 — 로그인 직후에는 isLoggedIn이 아직 갱신되기 전일 수 있어
  // handleBookmark를 다시 부르면 모달이 또 열린다.
  const toggleScrap = async () => {
    if (isScrapped) {
      unscrap(id);
      setToast({ message: '저장을 해제했어요' });
      try { await unscrapCampaign(id); }
      catch {
        scrap(campaign);
        setToast({ message: '잠시 후 다시 시도해주세요' });
      }
    } else {
      scrap(campaign);
      setToast({ message: '저장했어요' });
      try { await scrapCampaign(id); }
      catch {
        unscrap(id);
        setToast({ message: '잠시 후 다시 시도해주세요' });
      }
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => setToast({ message: '링크가 복사됐어요' }));
  };

  const handleApply = () => {
    if (campaign?.externalUrl) {
      pushEvent('campaign_cta_click', { campaign_id: id, campaign_title: campaign?.title });
      window.open(campaign.externalUrl, '_blank', 'noopener');
    }
  };

  if (loading) return <DetailSkeleton />;

  // 막다른 길을 만들지 않는다 — 재시도와 목록으로 빠져나갈 길을 같이 준다
  if (error || !campaign) {
    return (
      <div className={styles.state}>
        <p className={styles.stateText}>{error ?? '체험단을 찾을 수 없습니다.'}</p>
        <div className={styles.stateActions}>
          {error && (
            <button className={styles.retryBtn} onClick={() => setReloadKey((k) => k + 1)}>
              다시 시도
            </button>
          )}
          <Link className={styles.stateLink} to="/">체험단 목록으로</Link>
        </div>
      </div>
    );
  }

  const {
    title, brandName, channelType, channelTypes,
    recruitCount, recruitPeriod, announceDate, shipDate,
    reviewStart, reviewDeadline, reviewPeriod, content,
    imageUrls, images = [], detailImageUrls, detailImages = [],
    status,
  } = campaign;

  const toUrls = (arr) => (arr ?? []).map((item) => (typeof item === 'string' ? item : item?.url)).filter(Boolean);
  const heroUrls = toUrls(imageUrls ?? images);
  const detailImgUrls = toUrls(detailImageUrls ?? detailImages);
  const channels = channelTypes?.length ? channelTypes : (channelType ? [channelType] : []);
  const isClosed = status === 'COMPLETED';
  const dday = isClosed ? null : calcDday(recruitPeriod);

  // 서버는 빈 값을 null 또는 '-'로 섞어 내려준다
  const clean = (v) => (v && v !== '-' ? v : null);

  // 값이 없는 행은 그리지 않는다. 다만 배송일·리뷰 기간은 신청 전에 꼭 확인하는
  // 정보라, 아직 안 정해졌다는 사실 자체가 답이 된다. 비어 있어도 '-'로 남긴다.
  const infoRows = [
    { label: '모집 인원', value: recruitCount ? `${recruitCount}명` : '인원 미정' },
    { label: '모집 기간', value: clean(recruitPeriod) },
    { label: '선정 발표', value: clean(fmt(announceDate)) },
    { label: '배송일', value: clean(fmt(shipDate)), fallback: '-' },
    {
      label: '리뷰 기간',
      value: clean(reviewPeriod) ?? formatDateRange(reviewStart, reviewDeadline),
      fallback: '-',
    },
  ]
    .map((r) => (r.value ? r : { ...r, value: r.fallback, muted: true }))
    .filter((r) => r.value);

  const actions = (
    <>
      <button
        className={`${styles.iconBtn}${isScrapped ? ` ${styles.iconBtnActive}` : ''}`}
        onClick={handleBookmark}
        aria-label={isScrapped ? '저장 취소' : '저장'}
      >
        <IconBookmark size={20} filled={isScrapped} />
      </button>
      <button className={styles.iconBtn} onClick={handleShare} aria-label="링크 복사">
        <IconShare size={18} />
      </button>
      <button
        className={`${styles.applyBtn}${isClosed ? ` ${styles.applyBtnClosed}` : ''}`}
        onClick={handleApply}
        disabled={isClosed}
      >
        {isClosed ? '모집 종료' : '공고 바로가기'}
      </button>
    </>
  );

  return (
    <>
      <div className={styles.page}>
        <div className={styles.inner}>
          <Breadcrumb parent={{ to: '/', label: '체험단' }} current={title} className={styles.crumb} />

          <div className={styles.top}>
            <Gallery urls={heroUrls} />

            <aside className={styles.panel}>
              <div className={styles.panelInner}>
                {brandName && <p className={styles.brand}>{brandName}</p>}
                <h1 className={styles.title}>{title}</h1>

                <div className={styles.badges}>
                  {isClosed ? (
                    <span className={`${styles.badge} ${styles.badgeClosed}`}>모집 종료</span>
                  ) : dday !== null && dday >= 0 ? (
                    <span className={`${styles.badge}${dday <= 3 ? ` ${styles.badgeUrgent}` : ''}`}>
                      {dday === 0 ? '오늘 마감' : `마감 D-${dday}`}
                    </span>
                  ) : null}
                  {channels.map((c) => (
                    <span key={c} className={`${styles.badge} ${styles.badgeChannel}`}>
                      {CHANNEL_LABEL[c] ?? c}
                    </span>
                  ))}
                </div>

                <dl className={styles.info}>
                  {infoRows.map((r) => (
                    <div className={styles.infoRow} key={r.label}>
                      <dt className={styles.infoLabel}>{r.label}</dt>
                      <dd className={`${styles.infoValue}${r.muted ? ` ${styles.infoValueMuted}` : ''}`}>
                        {r.value}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className={styles.actions}>{actions}</div>
              </div>
            </aside>
          </div>

          {content && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>체험단 소개</h2>
              <p className={styles.content}>{content}</p>
            </section>
          )}

          {detailImgUrls.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>상세 정보</h2>
              <div className={styles.detailImages}>
                {detailImgUrls.map((url, i) => (
                  <img key={i} src={url} alt={`상세 이미지 ${i + 1}`} className={styles.detailImg} loading="lazy" />
                ))}
              </div>
            </section>
          )}
        </div>

        {/* 모바일 — 하단 고정 액션 바 */}
        <div className={styles.mobileBar}>{actions}</div>
      </div>

      <LoginModal isOpen={isOpen} onClose={close} onSuccess={toggleScrap} />
      {toast && (
        <Toast message={toast.message} onDismiss={dismissToast} />
      )}
    </>
  );
}
